// components/common/StatusIndicator.js
import React from 'react';

export function StatusIndicator({ 
  status, 
  size = 'medium',
  showLabel = true,
  className = '',
  ...props 
}) {
  const statusConfig = {
    scheduled: {
      color: 'blue',
      label: 'Scheduled',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      dotColor: 'bg-blue-400'
    },
    confirmed: {
      color: 'green',
      label: 'Confirmed',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      dotColor: 'bg-green-400'
    },
    completed: {
      color: 'gray',
      label: 'Completed',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      dotColor: 'bg-gray-400'
    },
    cancelled: {
      color: 'red',
      label: 'Cancelled',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      dotColor: 'bg-red-400'
    },
    'no-show': {
      color: 'orange',
      label: 'No Show',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
      dotColor: 'bg-orange-400'
    },
    active: {
      color: 'green',
      label: 'Active',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      dotColor: 'bg-green-400'
    },
    inactive: {
      color: 'gray',
      label: 'Inactive',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      dotColor: 'bg-gray-400'
    }
  };
  
  const config = statusConfig[status] || statusConfig.scheduled;
  
  const sizeClasses = {
    small: {
      container: 'px-2 py-0.5 text-xs',
      dot: 'w-1.5 h-1.5'
    },
    medium: {
      container: 'px-2.5 py-0.5 text-sm',
      dot: 'w-2 h-2'
    },
    large: {
      container: 'px-3 py-1 text-sm',
      dot: 'w-2.5 h-2.5'
    }
  };
  
  const baseClasses = 'inline-flex items-center font-medium rounded-full';
  const containerClasses = `${baseClasses} ${config.bgColor} ${config.textColor} ${sizeClasses[size].container} ${className}`;
  
  if (!showLabel) {
    // Just show the dot
    return (
      <span 
        className={`${config.dotColor} ${sizeClasses[size].dot} rounded-full ${className}`}
        title={config.label}
        {...props}
      />
    );
  }
  
  return (
    <span className={containerClasses} {...props}>
      <span className={`${config.dotColor} ${sizeClasses[size].dot} rounded-full mr-1.5`} />
      {config.label}
    </span>
  );
}

// Specialized status indicator for appointments
export function AppointmentStatus({ status, size = 'medium', showLabel = true }) {
  return (
    <StatusIndicator 
      status={status} 
      size={size} 
      showLabel={showLabel}
    />
  );
}

// Specialized status indicator for services
export function ServiceStatus({ isActive, size = 'medium', showLabel = true }) {
  const status = isActive ? 'active' : 'inactive';
  return (
    <StatusIndicator 
      status={status} 
      size={size} 
      showLabel={showLabel}
    />
  );
}

// Status indicator with custom colors
export function CustomStatusIndicator({ 
  label, 
  color = 'gray', 
  size = 'medium',
  showDot = true,
  className = '',
  ...props 
}) {
  const colorClasses = {
    gray: { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-400' },
    red: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-400' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-400' },
    green: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-400' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-400' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-400' },
    pink: { bg: 'bg-pink-100', text: 'text-pink-800', dot: 'bg-pink-400' }
  };
  
  const sizeClasses = {
    small: { container: 'px-2 py-0.5 text-xs', dot: 'w-1.5 h-1.5' },
    medium: { container: 'px-2.5 py-0.5 text-sm', dot: 'w-2 h-2' },
    large: { container: 'px-3 py-1 text-sm', dot: 'w-2.5 h-2.5' }
  };
  
  const colors = colorClasses[color] || colorClasses.gray;
  const sizes = sizeClasses[size];
  
  const baseClasses = 'inline-flex items-center font-medium rounded-full';
  const containerClasses = `${baseClasses} ${colors.bg} ${colors.text} ${sizes.container} ${className}`;
  
  return (
    <span className={containerClasses} {...props}>
      {showDot && (
        <span className={`${colors.dot} ${sizes.dot} rounded-full mr-1.5`} />
      )}
      {label}
    </span>
  );
}